import React from "react";
import { profileData } from "../../data/mock";
import { Cpu, Workflow, GitBranch, Gauge, ChevronRight } from "lucide-react";

const highlights = [
  {
    icon: Cpu,
    title: "Data Architecture",
    desc: "Conceptual, logical & physical data modeling for enterprise systems",
    code: "model.build()",
  },
  {
    icon: Workflow,
    title: "Warehousing",
    desc: "Snowflake-based data warehousing with dimensional modeling",
    code: "warehouse.init()",
  },
  {
    icon: GitBranch,
    title: "Collaboration",
    desc: "Cross-functional team alignment on data solutions",
    code: "team.sync()",
  },
  {
    icon: Gauge,
    title: "Optimization",
    desc: "Query performance tuning and reporting efficiency",
    code: "query.optimize()",
  },
];

const AboutSection = () => {
  return (
    <section id="about" className="relative">
      <div className="hex-pattern absolute inset-0 pointer-events-none" />
      <div className="section-container relative z-10">
        <div className="grid md:grid-cols-2 gap-16 items-start">
          {/* Left - Text */}
          <div>
            <span className="section-label">About</span>
            <h2 className="section-title">
              Crafting Data Models
              <br />
              <span className="bg-gradient-to-r from-[#06B6D4] to-[#0891B2] bg-clip-text text-transparent">
                That Scale
              </span>
            </h2>
            <div className="gradient-line mb-6" />

            {/* Terminal-style summary */}
            <div className="terminal-card mb-6">
              <div className="terminal-header">
                <div className="terminal-dot bg-[#EF4444]" />
                <div className="terminal-dot bg-[#F59E0B]" />
                <div className="terminal-dot bg-[#22C55E]" />
                <span className="ml-2 text-[10px] text-[#475569] font-['Fira_Code']">
                  about.md
                </span>
              </div>
              <div className="p-5">
                <p className="text-[#94A3B8] leading-relaxed text-[0.9rem]">
                  {profileData.summary}
                </p>
              </div>
            </div>

            {/* Languages as inline tags */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] text-[#475569] font-['Fira_Code'] mr-1">
                languages:
              </span>
              {["English", "Bengali", "Hindi"].map((lang) => (
                <span
                  key={lang}
                  className="px-2.5 py-1 rounded text-[10px] bg-[#0A1628] border border-[#1E293B] text-[#64748B] font-['Fira_Code'] hover:border-[#06B6D4]/30 hover:text-[#06B6D4] transition-colors duration-200"
                >
                  {lang}
                </span>
              ))}
            </div>
          </div>

          {/* Right - Highlight Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {highlights.map((item, i) => (
              <div
                key={i}
                className="glass-card p-5 group hover:border-[#06B6D4]/25 transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-[#06B6D4]/10 flex items-center justify-center group-hover:bg-[#06B6D4]/15 transition-colors duration-300">
                    <item.icon size={17} className="text-[#06B6D4]" />
                  </div>
                  <h4 className="text-sm font-semibold text-[#F1F5F9] font-['Figtree']">
                    {item.title}
                  </h4>
                </div>
                <p className="text-xs text-[#64748B] leading-relaxed mb-3">
                  {item.desc}
                </p>
                <div className="flex items-center gap-1.5 text-[10px] font-['Fira_Code'] text-[#334155] group-hover:text-[#06B6D4]/60 transition-colors">
                  <ChevronRight size={10} />
                  {item.code}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
